import { Footer } from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import { Container } from "@/components/ui/Container";
import { Ornament } from "@/components/ui/Ornament";
import { seededEvents } from "@/data/seed-imam-ali-2026";
import { api } from "@/lib/api-client";
import { type EventSummary, summarizeEvent } from "@/lib/event-schema";
import { ArrowUpRight, Calendar, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// The error branch falls back to the bundled seed so the home page
// never renders empty; the consumer renders an inline notice so the
// degradation is visible rather than silent.
type EventsState =
  | { status: "loading" }
  | { status: "ready"; events: EventSummary[] }
  | { status: "error"; events: EventSummary[] };

export function PlatformHome() {
  const [state, setState] = useState<EventsState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    api.events
      .list()
      .then(async (summaries) => {
        // Summaries omit tagline + banner; fetch each full doc to populate the card.
        const docs = await Promise.all(
          summaries.map((s) =>
            api.events
              .get(s.slug)
              .then((doc) => summarizeEvent(doc))
              .catch((err) => {
                console.error("PlatformHome: failed to load event doc", {
                  slug: s.slug,
                  err,
                });
                return fallbackSummary(s);
              }),
          ),
        );
        if (cancelled) return;
        setState({ status: "ready", events: docs.filter((e) => e.status === "published") });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("PlatformHome: events list failed; falling back to seed", err);
        const fallback = Object.values(seededEvents)
          .map(summarizeEvent)
          .filter((e) => e.status === "published");
        setState({ status: "error", events: fallback });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Navigation
        entries={[
          { label: "Events", to: "/#events" },
          { label: "Donate", to: "/donate" },
          { label: "About", to: "/about" },
        ]}
        homePath="/"
      />
      <main className="flex-1">
        <Hero />
        <EventsGrid state={state} />
        <ValueSection />
      </main>
      <Footer />
    </>
  );
}

function fallbackSummary(s: {
  slug: string;
  title: string;
  status: "draft" | "published";
  date?: string;
}): EventSummary {
  const seeded = seededEvents[s.slug];
  if (seeded) return summarizeEvent(seeded);
  return {
    slug: s.slug,
    title: s.title,
    status: s.status,
    date: s.date,
  };
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(700px 380px at 80% 10%, rgb(var(--accent) / 0.30), transparent 50%), radial-gradient(600px 320px at 0% 20%, rgb(var(--accent) / 0.18), transparent 60%)",
        }}
      />
      <Container size="wide" className="pt-16 sm:pt-20 lg:pt-28 pb-12 lg:pb-20">
        <p className="eyebrow text-accent inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
          Mobeen Center
        </p>
        <h1 className="display-title mt-5 max-w-4xl text-balance text-[clamp(2.6rem,7vw,5.75rem)] leading-[0.96]">
          A home for our <em className="not-italic text-accent">community gatherings</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-muted">
          Programs, registrations, potlucks, competitions, and giving — composed from modular pieces
          so each event feels considered, not templated.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/imam-ali-2026"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas dark:bg-accent dark:text-accent-on shadow-glow hover:bg-ink/90 dark:hover:bg-accent/90 transition-colors focus-ring"
          >
            See the next event
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 rounded-full border border-line/80 px-5 py-3 text-sm font-medium text-ink hover:border-accent hover:text-accent transition-colors focus-ring"
          >
            Organizer sign-in
          </Link>
        </div>
      </Container>
    </section>
  );
}

function EventsGrid({ state }: { state: EventsState }) {
  return (
    <section id="events" className="relative">
      <Container size="wide" className="py-16 lg:py-24">
        <header className="mb-10 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow text-accent">Upcoming & published</p>
            <h2 className="display-title mt-3 text-[clamp(1.8rem,3.6vw,2.75rem)] leading-tight">
              Events on the platform
            </h2>
          </div>
          <Link to="/admin" className="text-sm text-muted hover:text-accent transition-colors">
            Create a new event →
          </Link>
        </header>
        {state.status === "loading" ? (
          <div className="grid h-32 place-items-center text-muted">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
          </div>
        ) : (
          <>
            {state.status === "error" ? (
              <p className="mb-6 rounded-lg border border-line/70 bg-surface px-4 py-2.5 text-xs text-muted">
                Live event data is unavailable right now; showing the most recent cached set.
              </p>
            ) : null}
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {state.events.map((e) => (
                <EventCard key={e.slug} event={e} />
              ))}
              <ComposableCard />
            </div>
          </>
        )}
      </Container>
    </section>
  );
}

function EventCard({ event }: { event: EventSummary }) {
  return (
    <Link
      to={`/${event.slug}`}
      className="group surface-card relative overflow-hidden transition-all hover:border-accent/50 hover:shadow-glow focus-ring"
    >
      {event.banner?.src ? (
        <div className="relative aspect-[16/9] overflow-hidden bg-paper">
          <img
            src={event.banner.src}
            alt={event.banner.alt || event.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
      ) : (
        <div
          aria-hidden
          className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-accent/12 blur-3xl transition-transform group-hover:scale-110"
        />
      )}
      <div className="p-6">
        <span className="eyebrow text-accent inline-flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} />
          {event.date ?? "Date TBA"}
        </span>
        <h3 className="mt-3 font-display text-2xl font-light leading-snug text-ink">
          {event.title}
        </h3>
        {event.tagline ? (
          <p className="mt-2 text-sm text-muted leading-relaxed">{event.tagline}</p>
        ) : null}
        <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent">
          View event
          <ArrowUpRight
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            strokeWidth={1.75}
          />
        </span>
      </div>
    </Link>
  );
}

function ComposableCard() {
  return (
    <div className="surface-card relative overflow-hidden p-6 flex flex-col items-start justify-between bg-paper">
      <span className="eyebrow text-accent">Composable</span>
      <p className="mt-3 font-display text-xl font-light leading-snug text-ink">
        Spin up your next gathering in minutes — toggle modules, upload a banner, publish.
      </p>
      <Link
        to="/admin"
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
      >
        Open the editor →
      </Link>
    </div>
  );
}

function ValueSection() {
  const pillars = [
    {
      eyebrow: "Modular",
      title: "Pick what you need",
      body: "RSVP, potluck, competition, junior program, donation campaign, toggle each one independently per event.",
    },
    {
      eyebrow: "Considered",
      title: "Composed, not templated",
      body: "Every page is rendered from a single document. The design adapts to the event, not the other way around.",
    },
    {
      eyebrow: "Effortless",
      title: "One panel for everything",
      body: "Upload a banner, fill in the day, share the link. The image gets optimized automatically for fast loads.",
    },
  ];
  return (
    <section className="relative">
      <Container size="wide" className="pb-24">
        <Ornament className="mb-16" />
        <div className="grid gap-10 lg:grid-cols-3">
          {pillars.map((p, i) => (
            <article
              key={p.title}
              className="animate-rise"
              style={{ animationDelay: `${i * 90}ms` }}
            >
              <p className="eyebrow text-accent">{p.eyebrow}</p>
              <h3 className="mt-3 font-display text-2xl font-light leading-tight text-ink">
                {p.title}
              </h3>
              <p className="mt-3 text-pretty text-muted leading-relaxed">{p.body}</p>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}
